import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const fieldTypeRow = z.object({
  title: z.string(),
  key: z.string(),
  value: z.string(),
  target_type: z.string(),
  data_type: z.string()
});

export const useAutomationFormSchema = () => {
  const { t } = useTranslation("product", {
    keyPrefix: "automation.form.validations"
  });

  const schema = useMemo(
    () =>
      z
        .object({
          product_id: z.string().min(1, t("productRequired")),
          product_name: z.string(),
          host: z.string().min(1, t("hostRequired")),
          host_auth: z.string(),
          auth_type: z.string().min(1, t("authTypeRequired")),
          auth_header_name: z.string(),
          auth_value: z.string(),
          token_response_field: z.string(),
          delivery_type: z.string(),
          endpoints: z.array(
            z.object({
              step_type: z.string(),
              step_name: z.string(),
              content_type: z.string(),
              method: z.string(),
              endpoint_provisioning: z.string(),
              body_fill_customer: z.array(fieldTypeRow),
              body_static_system: z.array(fieldTypeRow),
              body_static_value: z.array(fieldTypeRow),
              next_endpoint_fields: z.array(fieldTypeRow)
            })
          )
        })
        .superRefine((data, ctx) => {
          if (!data.endpoints.length) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["endpoints"],
              message: t("endpointsRequired")
            });
            return;
          }

          data.endpoints.forEach((ep, epIdx) => {
            if (!ep.step_type.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["endpoints", epIdx, "step_type"],
                message: t("stepTypeRequired")
              });
            }
            if (!ep.step_name.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["endpoints", epIdx, "step_name"],
                message: t("stepNameRequired")
              });
            }
            if (!ep.method.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["endpoints", epIdx, "method"],
                message: t("methodRequired")
              });
            }
            if (!ep.endpoint_provisioning.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["endpoints", epIdx, "endpoint_provisioning"],
                message: t("endpointProvisioningRequired")
              });
            }
          });
        }),
    [t]
  );

  return schema;
};

export type AutomationFormSchema = z.infer<
  ReturnType<typeof useAutomationFormSchema>
>;

export const automationFormSchema = z.object({
  product_id: z.string().min(1),
  product_name: z.string(),
  host: z.string().min(1),
  host_auth: z.string(),
  auth_type: z.string().min(1),
  auth_header_name: z.string(),
  auth_value: z.string(),
  token_response_field: z.string(),
  delivery_type: z.string(),
  endpoints: z.array(
    z.object({
      step_type: z.string(),
      step_name: z.string(),
      content_type: z.string(),
      method: z.string(),
      endpoint_provisioning: z.string(),
      body_fill_customer: z.array(fieldTypeRow),
      body_static_system: z.array(fieldTypeRow),
      body_static_value: z.array(fieldTypeRow),
      next_endpoint_fields: z.array(fieldTypeRow)
    })
  )
});
